import React, { useEffect, useState } from 'react';
import { compose, graphql } from 'react-apollo';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingAvatar from 'assets/images/coaching/coaching_avatar.svg';

import Navigation from './Navigation';
import { NavigationSectionProps } from './NavigationSection';
import NEW_NAVIGATION_LIST from './NewNavigationList.gql';
import UNREADS from './Unreads.gql';
import { NewNavigationList } from './__generated__/NewNavigationList';
import { Unreads } from './__generated__/Unreads';

type NavigationWrapperProps = {
  navigationList: { loading: boolean; error: Error } & NewNavigationList;
  unreads: { loading: boolean; error: Error; refetch: () => void } & Unreads;
};

type NavigationSectionPropsArray = ({ key: string } & NavigationSectionProps)[];

const NavigationWrapper: React.FC<NavigationWrapperProps> = props => {
  const { loading, error, navigationList } = props.navigationList;
  const { loading: loadingUnreads, me } = props.unreads;

  const [sections, setSections] = useState<NavigationSectionPropsArray>([]);
  const [myUnreads, setMyUnreads] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    props.unreads.refetch();
  }, [navigationList]);

  useEffect(() => {
    const organizationUnreads: Record<string, number> =
      me?.unreads?.organization_unreads.reduce<Record<string, number>>((previousValue, item) => {
        previousValue[item.id] = item.unread_count;
        return previousValue;
      }, {}) ?? {};

    const groupUnreads: Record<string, number> =
      me?.unreads?.group_unreads.reduce<Record<string, number>>((previousValue, item) => {
        previousValue[item?.id ?? ''] = item?.unread_count ?? 0;

        // Staffclass
        if (item?.id) {
          const org = me?.organizations.find(org => org.class?.uuid === item.id);
          if (org) {
            previousValue[org.uuid ?? ''] = item.unread_count;
          }
        }

        return previousValue;
      }, {}) ?? {};

    const unreads: Record<string, number> =
      me?.unreads?.unreads?.reduce<Record<string, number>>((previousValue, item) => {
        previousValue[item?.id ?? ''] = item?.unread_count ?? 0;
        return previousValue;
      }, {}) ?? {};

    setMyUnreads({
      ...organizationUnreads,
      ...groupUnreads,
      ...unreads,
    });
  }, [me]);

  useEffect(() => {
    const organizations: NavigationSectionPropsArray =
      navigationList?.items?.reduce<NavigationSectionPropsArray>((previousValue, item) => {
        if (item.__typename === 'TextHeader') {
          return [
            ...previousValue,
            {
              key: item.text,
              title: item.text,
              items: [],
            },
          ];
        }

        if (item.__typename === 'NavigationListEntityItem') {
          const lastItem = previousValue[previousValue.length - 1];
          const isOrg = item.type === 'organization';

          lastItem.items = [
            ...lastItem.items,
            {
              key: item.uuid,
              title: item.title,
              image: (item.avatar as any).imageUrl,
              to: `/${isOrg ? 'organizations' : 'classes'}/${item.uuid}`,
              unreadCount: myUnreads[item.uuid] ?? 0,
            },
          ];
        }

        return previousValue;
      }, []) ?? [];

    const coachingStreams: NavigationSectionPropsArray = navigationList?.coaching
      ?.shouldShowCoachManagement
      ? [
          {
            key: 'coachingStreams',
            items: [
              {
                key: '1',
                title: 'Coach dashboard',
                image: CoachingAvatar,
                to: CoachingConstants.COACHING_SCHEDULE_ROUTE,
                unreadCount: 0,
              },
              ...(navigationList?.coaching?.coachingStreams.map(item => ({
                key: item.uuid,
                title: item.studentName ?? '',
                to: `/coaching/messages/${item.uuid}`,
                unreadCount: myUnreads[item.uuid] ?? 0,
              })) ?? []),
            ],
          },
        ]
      : [];

    const participantStreams: NavigationSectionPropsArray = navigationList?.coaching
      ?.shouldShowParticipantManagement
      ? [
          {
            key: 'participantStreams',
            items: [
              {
                key: '1',
                title: 'Remind Tutoring',
                image: CoachingAvatar,
                to: CoachingConstants.COACHING_HOME_ROUTE,
                unreadCount: 0,
              },
              ...(navigationList?.coaching?.participantStreams.map(item => ({
                key: item.uuid,
                title: item.studentName ?? '',
                to: `/coaching/messages/${item.uuid}`,
                unreadCount: myUnreads[item.uuid] ?? 0,
              })) ?? []),
            ],
          },
        ]
      : [];

    setSections([...coachingStreams, ...participantStreams, ...organizations]);
  }, [navigationList, myUnreads]);

  return (
    <Navigation
      loading={loading || loadingUnreads}
      error={Boolean(error)}
      sections={sections}
      allUnreads={myUnreads['user'] ?? 0}
    />
  );
};

const ContactInfoCompose = compose(
  graphql(NEW_NAVIGATION_LIST, {
    name: 'navigationList',
  }),
  graphql(UNREADS, {
    name: 'unreads',
  })
)(NavigationWrapper) as React.FC;

export default ContactInfoCompose;
